<template>
  <v-container id="credentials_root" fluid>
    <!-- DIALOG -->
    <router-view name="dialog"></router-view>

    <!-- CONTENT -->
    <v-row class="mb-6 px-6" align="center" justify="center">
      <v-col cols="7">
        <span class="text-h5">
          {{ $t('navigationMenu.credentialsAllAdmin') }}
        </span>
      </v-col>

      <v-col class="d-flex flex-row align-self-end" align="sretch">
        <div class="d-flex flex-row align-self-end">
          <v-btn id="create_credential_button" class="align-self-end mr-8" color="primary" to="/credentials/add">
            <v-icon left>mdi-plus-circle-outline</v-icon>
            <span class="ml-2 text-capitalize">{{ $t('credentials.content.createCredential') }}</span>
          </v-btn>
        </div>

        <v-text-field
          id="search" v-model="search" :label="$t('dataTable.search')" append-icon="mdi-magnify" single-line
          hide-details clearable
        >
        </v-text-field>
      </v-col>
    </v-row>

    <v-card class="pa-5">
      <v-data-table
        id="credentials_table" must-sort single-expand show-expand :headers="_headers"
        :items="credentialList" :page="pageNum" :options.sync="pagination" :server-items-length="totalItems"
        :loading="dataTableLoading" :loading-text="$t('dataTable.content.loadingPleaseWait')"
        :item-class="() => 'credentialsRow'"
        :no-data-text="$t('dataTable.content.noDataAvailable')" :footer-props="{
          'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
          'page-text': $t('dataTable.footer.pageText'),
          'items-per-page-options': [10, 25, 50]
        }" @update:page="pageChange" @update:sort-by="sortTable" @update:sort-desc="sortTable"
      >
        <template #[`item.status`]="{ item }">
          <v-chip label :color="getChipStyle(item)">
            {{ getChipText(item) }}
          </v-chip>
        </template>

        <template #[`item.description`]="{ item }">
          <long-text id="description" :text="item.description" :max-char="!isAdmin ? 55 : 30" />
        </template>

        <template v-if="isAdmin" #[`item.user`]="{ item }">
          <long-text :text="`${item.user.firstName} ${item.user.lastName}`" />
        </template>

        <template v-if="isAdmin" #[`item.email`]="{ item }">
          <long-text :text="item.user.email" />
        </template>

        <template #[`item.keyPurpose`]="{ item }">
          <span id="key_purpose">{{ getKeyPurposeText(item) }} </span>
        </template>

        <template #[`item.authMode`]="{ item }">
          <span id="auth_mode">{{ getAuthModeText(item) }}</span>
        </template>

        <template #[`item.createdAt`]="{ item }">
          <span id="created_at" class="font-italic caption">{{ convertDate(item.createdAt) }}</span>
        </template>

        <template #[`expanded-item`]="{ item }">
          <td :colspan="headers.length">
            <v-row align="baseline">
              <v-col cols="4">
                <pre>
                  <v-card flat>
                    <div class="pb-3 text-uppercase">
                      {{ $t('dataTable.header.credentials.keyData') }}
                    </div>
                    <v-simple-table dense>
                      <template #default>
                        <tbody>
                          <tr>
                            <td>{{ $t('dataTable.header.credentials.keyAlg') }}</td>
                            <td>{{ item.keyAlg.toUpperCase() }}</td>
                          </tr>
                          <tr>
                            <td>{{ $t('dataTable.header.credentials.keySizeOrCurveName') }}</td>
                            <td>{{ item.rsaKeySize != 0 ? item.rsaKeySize : item.ecCurveName }}</td>
                          </tr>
                          <tr>
                            <td>{{ $t('dataTable.header.credentials.keyPurpose') }}</td>
                            <td>{{ getKeyPurposeText(item) }}</td>
                          </tr>
                          <tr v-if="item.preauthQuantity !== undefined">
                            <td>{{ $t('dataTable.header.credentials.preauthQuantity') }}</td>
                            <td>{{ item.preauthQuantity === undefined ? '-' : item.preauthQuantity }}</td>
                          </tr>
                          <tr v-if="item.preauthUntil !== undefined">
                            <td>{{ $t('dataTable.header.credentials.preauthUntil') }}</td>
                            <td>{{ item.preauthUntil === undefined ? '-' : item.preauthUntil }}</td>
                          </tr>
                        </tbody>
                      </template>
</v-simple-table>
</v-card>
</pre>
              </v-col>
              <v-col v-if="item.certChain.length > 0">
                <pre>
    <v-card flat>
      <div class="pb-3 text-uppercase">
        {{ $t('dataTable.header.credentials.certificateData') }}
      </div>
      <v-simple-table dense>
        <template #default>
                        <tbody>
                          <tr>
                            <td>{{ $t('dataTable.header.credentials.certificates.issuedTo') }}</td>
                            <td>{{ item.certChain[0].issuedTo }}</td>
                          </tr>
                          <tr>
                            <td>{{ $t('dataTable.header.credentials.certificates.issuedBy') }}</td>
                            <td>{{ item.certChain[0].issuedBy }}</td>
                          </tr>
                          <tr>
                            <td>{{ $t('dataTable.header.credentials.certificates.validFrom') }}</td>
                            <td>{{ convertDate(item.certChain[0].validFrom) }}</td>
                          </tr>
                          <tr>
                            <td>{{ $t('dataTable.header.credentials.certificates.validTo') }}</td>
                            <td>{{ convertDate(item.certChain[0].validTo) }}</td>
                          </tr>
                          <tr>
                            <td>{{ $t('dataTable.header.credentials.certificates.serialNum') }}</td>
                            <td>{{ item.certChain[0].serialNum }}</td>
                          </tr>
                        </tbody>
                      </template>
      </v-simple-table>
    </v-card>
  </pre>
              </v-col>
            </v-row>
          </td>
        </template>

        <template #[`item.actions`]="{ item, on }">
          <v-btn
            v-if="isCreatedCredential(item.status) && showPasswordActions(item)" id="activate" color="primary"
            :loading="credentialTableActivationBtnLoading[item.id]" @click=" isImplicitCredential(item.authMode)
              ? activateCredential(item) : showAuthorizationModal(item, credentialOperation.ACTIVATE)"
          >
            {{ $t('dataTable.content.action.credentials.activation') }}
          </v-btn>

          <div class="d-inline" v-on="on">
            <v-btn
              id="update" class="pa-1 " text
              :to="'/credentials/' + (isAdmin ? item.user.id + '/' + item.id : item.id) + '/update'"
            >
              <v-icon>
                mdi-cog-outline
              </v-icon>
              {{ $t('dataTable.content.action.update') }}
            </v-btn>
          </div>

          <v-btn
            id="certificates" text class="pa-1 "
            :to="'/credentials/' + (isAdmin ? item.user.id + '/' + item.id : item.id) + '/certificates'"
          >
            <v-icon>mdi-certificate-outline</v-icon>
            {{ $t('dataTable.content.action.credentials.showCertificates') }}
          </v-btn>

          <v-menu offset-y absolute :close-on-content-click="false">
            <template #activator="{ on: menuTrigger }">
              <v-btn id="more" text class="pa-1 " v-on="menuTrigger">
                <v-icon>mdi-dots-horizontal</v-icon>
                {{ $t('dataTable.content.action.more') }}
              </v-btn>
            </template>
            <v-list id="actions_list">
              <template v-if="isActivatedCredential(item.status) && showPasswordActions(item)">
                <v-list-item
                  id="change_password"
                  @click="isImplicitCredential(item.authMode)
                    ? changeCredentialPassword(item) : showAuthorizationModal(item, credentialOperation.CHANGE_PASSWORD)"
                >
                  <v-list-item-title>{{ $t('dataTable.content.action.credentials.changePassword') }}</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-lock-reset</v-icon>
                  </v-list-item-icon>
                </v-list-item>

                <v-list-item
                  id="unlock" @click="isImplicitCredential(item.authMode)
                    ? unlockCredential(item) : showAuthorizationModal(item, credentialOperation.UNLOCK)"
                >
                  <v-list-item-title>{{ $t('dataTable.content.action.credentials.unlock') }}</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-lock-open-variant</v-icon>
                  </v-list-item-icon>
                </v-list-item>

                <v-list-item
                  id="change_puk" @click="isImplicitCredential(item.authMode)
                    ? changeCredentialPuk(item) : showAuthorizationModal(item, credentialOperation.CHANGE_PUK)"
                >
                  <v-list-item-title>{{ $t('dataTable.content.action.credentials.changePuk') }}</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-lock-reset</v-icon>
                  </v-list-item-icon>
                </v-list-item>

                <v-list-item id="clear_preauth" @click="clearCredentialPreauthorization(item.id, $event)">
                  <v-list-item-title>{{ $t('dataTable.content.action.credentials.clearPreAuth') }}</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </template>

              <div class="d-inline" v-on="on">
                <v-list-item id="delete" @click="deleteCredential(item.id, $event)">
                  <v-list-item-title>{{ $t('dataTable.content.action.delete') }}</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-delete</v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </div>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- authorizationModal -->
    <v-row class="authorizationModal" justify="center">
      <v-dialog v-model="authorizationModal" persistent max-width="500">
        <v-card id="explicit_authorization_modal">
          <v-card-title>
            <span class="headline">{{ getCredentialOperationTitle(selectedCredentialOperation) }}</span>
          </v-card-title>
          <v-form ref="authorizationModalForm" v-model="authorizationModalFormValid" @submit.prevent>
            <v-card-text>
              <!-- changePassword -->
              <v-text-field
                v-if="isChangePasswordCredentialOperation(selectedCredentialOperation)" id="old_pwd"
                ref="oldPwdField" v-model="expAuthPwd.oldPwd" tabindex="1"
                :append-icon="expAuthPwd.showOldPwd ? 'mdi-eye' : 'mdi-eye-off'"
                :label="$t('credentials.modal.authorization.content.oldKeyPassword') + `*`"
                :type="expAuthPwd.showOldPwd ? 'text' : 'password'" prepend-icon="mdi-lock"
                :rules="[rules.required, rules.passwordMustBeLongerThan, rules.onlyNumber]"
                @click:append="expAuthPwd.showOldPwd = !expAuthPwd.showOldPwd"
              />

              <!-- unlock -->
              <v-text-field
                v-if="isUnlockCredentialOperation(selectedCredentialOperation)" id="old_pwd"
                ref="oldPwdField" v-model="expAuthPwd.oldPwd" tabindex="1"
                :append-icon="expAuthPwd.showOldPwd ? 'mdi-eye' : 'mdi-eye-off'"
                :label="$t('credentials.modal.authorization.content.keyPuk') + `*`"
                :type="expAuthPwd.showOldPwd ? 'text' : 'password'" prepend-icon="mdi-lock" :rules="[rules.required]"
                @click:append="expAuthPwd.showOldPwd = !expAuthPwd.showOldPwd"
              />

              <!-- changePuk -->
              <v-text-field
                v-if="isChangePukCredentialOperation(selectedCredentialOperation)" id="old_pwd"
                ref="oldPwdField" v-model="expAuthPwd.oldPwd" tabindex="1"
                :append-icon="expAuthPwd.showOldPwd ? 'mdi-eye' : 'mdi-eye-off'"
                :label="$t('credentials.modal.authorization.content.oldKeyPuk') + `*`"
                :type="expAuthPwd.showOldPwd ? 'text' : 'password'" prepend-icon="mdi-lock" :rules="[rules.required]"
                @click:append="expAuthPwd.showOldPwd = !expAuthPwd.showOldPwd"
              />

              <v-text-field
                id="password1" ref="pwdField" v-model="expAuthPwd.pwd" tabindex="2"
                :append-icon="expAuthPwd.showPwd ? 'mdi-eye' : 'mdi-eye-off'"
                :label="getPasswordFieldLabel(selectedCredentialOperation)"
                :type="expAuthPwd.showPwd ? 'text' : 'password'" prepend-icon="mdi-lock"
                :rules="[rules.required, rules.passwordMustBeLongerThan, rules.onlyNumber]"
                @click:append="expAuthPwd.showPwd = !expAuthPwd.showPwd"
              />

              <v-text-field
                id="password2" v-model="expAuthPwd.rePwd" tabindex="3"
                :append-icon="expAuthPwd.showRePwd ? 'mdi-eye' : 'mdi-eye-off'"
                :label="getPassworConfirmationFieldLabel(selectedCredentialOperation)"
                :type="expAuthPwd.showRePwd ? 'text' : 'password'" prepend-icon="mdi-lock"
                :rules="[rules.required, rules.passwordMustBeLongerThan, rules.onlyNumber, rules.passwordMatch]"
                @click:append="expAuthPwd.showRePwd = !expAuthPwd.showRePwd"
              />
              <v-text-field
                v-if="isChangePukCredentialOperation(selectedCredentialOperation) && !!selectedCredential.xkblMode"
                id="keyPassword1" ref="pwdField" v-model="keyPwd.pwd" tabindex="2"
                :append-icon="keyPwd.showPwd ? 'mdi-eye' : 'mdi-eye-off'"
                :label="$t('credentials.modal.authorization.content.newKeyPassword')"
                :type="keyPwd.showPwd ? 'text' : 'password'" prepend-icon="mdi-key"
                :rules="[rules.required, rules.passwordMustBeLongerThan, rules.onlyNumber]"
                @click:append="keyPwd.showPwd = !keyPwd.showPwd"
              />
            </v-card-text>
            <v-card-actions>
              <v-btn id="cancel" text tabindex="5" @click="authorizationModal = false">{{ $t('main.cancel') }}</v-btn>

              <!-- activation -->
              <v-btn
                v-if="isActiveCredentialOperation(selectedCredentialOperation)" id="confirm" tabindex="4"
                color="success" :disabled="!authorizationModalFormValid" :loading="authorizationModalBtnLoading"
                type="submit" @click="activateCredential()"
              >
                {{ $t('credentials.modal.authorization.content.button.activation') }}
              </v-btn>

              <!-- changePassword -->
              <v-btn
                v-if="isChangePasswordCredentialOperation(selectedCredentialOperation)" id="confirm" tabindex="5"
                color="success" :disabled="!authorizationModalFormValid" :loading="authorizationModalBtnLoading"
                type="submit" @click="changeCredentialPassword()"
              >
                {{ $t('credentials.modal.authorization.content.button.changePassword') }}
              </v-btn>

              <!-- unlock -->
              <v-btn
                v-if="isUnlockCredentialOperation(selectedCredentialOperation)" id="confirm" tabindex="5"
                color="success" :disabled="!authorizationModalFormValid" :loading="authorizationModalBtnLoading"
                type="submit" @click="unlockCredential()"
              >
                {{ $t('credentials.modal.authorization.content.button.unlock') }}
              </v-btn>

              <!-- changePuk -->
              <v-btn
                v-if="isChangePukCredentialOperation(selectedCredentialOperation)" id="confirm" tabindex="5"
                color="success" type="submit" :disabled="!authorizationModalFormValid"
                :loading="authorizationModalBtnLoading" @click="changeCredentialPuk()"
              >
                {{ $t('credentials.modal.authorization.content.button.changePuk') }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>
    </v-row>

    <!-- showGeneratedPukModal -->
    <v-row class="showGeneratedPukModal" justify="center">
      <v-dialog v-model="generatedPukModal" persistent max-width="500">
        <v-card id="puk_modal">
          <v-card-title>
            <span class="headline">{{ $t('credentials.modal.puk.title') }}</span>
          </v-card-title>
          <v-card-text>{{ $t('credentials.modal.puk.content.info') }}</v-card-text>
          <v-card-text id="puk" class="text-center headline">{{ formattedPuk }}</v-card-text>
          <v-card-actions>
            <v-btn id="ok" class="primary" text block @click="generatedPukModal = false">
              {{ $t('credentials.modal.puk.content.button.understood') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
    <v-row>
      <v-dialog v-model="showNoDevicesModal" justify="center" max-width="500">
        <v-card id="no_device_modal">
          <v-card-title>
            <span class="headline">{{ $t('credentials.modal.noDevice.title') }}</span>
          </v-card-title>
          <v-card-text>
            {{ $t('credentials.modal.noDevice.description') }}
          </v-card-text>
          <v-card-actions class="d-flex flex-row justify-center">
            <v-btn class="primary" to="/user/profile/devices/add">
              <v-icon>
                mdi-cellphone-key
              </v-icon>
              {{ $t('dashboard.addDevice') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>

    <implicit-authorization-modal
      :key="implicitAuthorizationModalKey"
      :visible="ImplicitAuthorizationModalVisible"
    ></implicit-authorization-modal>
  </v-container>
</template>

<script>
import debounce from 'lodash.debounce'
import { mapGetters } from 'vuex'
import Role from '../../Role'
import ImplicitAuthorizationModal from '../../components/ImplicitAuthorizationModal.vue'
import LongText from '../../components/LongText.vue'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../event-bus'
import credentials from '../../services/api/rsspUtil'
import AuthMode from './AuthMode'
import CredentialStatus from './CredentialStatus'

export default {
  name: 'CredentialsAdmin',

  components: {
    LongText,
    ImplicitAuthorizationModal
  },

  data() {
    return {
      headers: [],
      search: null,
      sortDesc: true,
      pageNum: 1,
      sortBy: ['createdAt'],
      pagination: {
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      passwordMinCharacters: 6,
      credentialList: [],
      totalItems: 0,
      isAdminUser: false,
      dataTableLoading: false,
      authorizationModalFormValid: false,
      credentialTableActivationBtnLoading: {},
      authorizationModal: false,
      authorizationModalBtnLoading: false,
      show: false,
      selectedCredential: null,
      selectedCredentialOperation: null,
      authorizationTimeoutMilis: this.$store.state.remoteConfig.authorizationTimeoutSecs * 1000,
      credentialOperation: {
        ACTIVATE: 'activate',
        CHANGE_PASSWORD: 'changePassword',
        UNLOCK: 'unlock',
        CHANGE_PUK: 'changePuk'
      },
      expAuthPwd: {
        oldPwd: null,
        showOldPwd: false,
        pwd: null,
        showPwd: false,
        rePwd: null,
        showRePwd: false
      },
      keyPwd: {
        pwd: null,
        showPwd: false
      },
      generatedPukModal: false,
      formattedPuk: null,
      rules: {
        required: v => !!v || this.$t('validation.form.requiredField'),
        passwordMatch: v => (v === this.expAuthPwd.pwd) || this.$t('validation.form.passwordMustBeIdentical'),
        // minLenght: v => (v && v.length >= 6) || 'This field must have at least 6 characters'
        passwordMustBeLongerThan: v => (v && v.length >= this.passwordMinCharacters) || this.$t('validation.form.passwordMustBeLongerThan', { min: this.passwordMinCharacters }),
        onlyNumber: v => (/^[0-9]+$/.test(v)) || this.$t('validation.form.onlyNumbers'),
        atLeasOneDigit: (value) => (value && /\d/.test(value)) || 'At least one digit',
        atLeasOneLowerCaseLetter: (value) => (value && /[a-z]{1}/.test(value)) || 'At least one lowercase letter',
        atLeasOneUpperCaseLetter: (value) => (value && /[A-Z]{1}/.test(value)) || 'At least one capital letter',
        atLeasOneSpecialCharacter: (value) => (value && /[^A-Za-z0-9]/.test(value)) || 'At least one special character'
      },
      passwordRules: [
        value => !!value || 'Please enter a password',
        (value) => !!value || 'Please type password.',
        (value) => (value === this.expAuthPwd.pwd) || this.$t('validation.form.passwordMustBeIdentical'),
        (value) => (value && /\d/.test(value)) || 'At least one digit',
        (value) => (value && /[a-z]{1}/.test(value)) || 'At least one lowercase letter',
        (value) => (value && /[A-Z]{1}/.test(value)) || 'At least one capital letter',
        (value) => (value && /[^A-Za-z0-9]/.test(value)) || 'At least one special character',
        (value) => (value && value.length < this.passwordMinCharacters) || this.$t('validation.form.passwordMustBeLongerThan', { min: this.passwordMinCharacters })
      ],
      showNoDevicesModal: false,
      // implicitAuthorizationModal
      implicitAuthorizationModalKey: 0,
      ImplicitAuthorizationModalVisible: false,
      devicesListTotalItems: 0,
      rsspUserId: null
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    },
    _headers() {
      /* const isAdmin = this.hasRole(Role.ADMIN)
      // todo search better solution to hide header column
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.headers[5].show = isAdmin
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.headers[6].show = isAdmin
      return this.headers.filter(x => x.show) */
      return this.headers.filter(x => x.show)
    }
  },

  watch: {
    search() {
      this.dataTableLoading = true
      this.searchCredentialsWatcher()
    },
    pagination: {
      handler(ev) {
        this.searchCredentials()
      }
    }
  },
  created() {
    this.headers = [
      {
        text: this.$t('dataTable.header.credentials.description'),
        align: 'left',
        value: 'description',
        show: true
      },
      {
        text: this.$t('dataTable.header.credentials.status'),
        align: 'left',
        value: 'status',
        show: true
      },
      {
        text: this.$t('dataTable.header.createdAt'),
        align: 'left',
        value: 'createdAt',
        show: true
      },
      {
        text: this.$t('dataTable.header.credentials.keyPurpose'),
        align: 'left',
        value: 'keyPurpose',
        show: true,
        sortable: false

      },
      {
        text: this.$t('dataTable.header.credentials.authMode'),
        align: 'left',
        value: 'authMode',
        show: true
      },
      {
        text: this.$t('dataTable.header.credentials.user'),
        align: 'left',
        value: 'user',
        show: this.isAdmin
      },
      {
        text: this.$t('dataTable.header.credentials.email'),
        align: 'left',
        value: 'email',
        show: this.isAdmin
      },
      {
        text: this.$t('dataTable.header.actions'),
        align: 'right',
        value: 'actions',
        sortable: false,
        show: true
      }
    ]
  },
  async mounted() {
    this.pageNum = this.$route.query.pageNum ? Number(this.$route.query.pageNum) : 1 || 1
    this.pagination.page = this.pageNum
    this.sortBy = this.$route.query.sortBy ? [this.$route.query.sortBy] : ['createdAt']
    this.pagination.sortBy = this.sortBy
    // eslint-disable-next-line no-unneeded-ternary
    this.pagination.sortDesc[0] = this.$route.query.sortOrder ? (this.$route.query.sortOrder === 'desc' ? true : false) : true

    this.searchCredentials()

    const rsspUser = await credentials.joinUserOnRssp()
    this.rsspUserId = rsspUser.id
  },

  methods: {
    gotoPage(page) {
      this.$nextTick(() => {
        this.pagination.page = page
      })
    },

    pageChange(newPage) {
      this.$router.replace({ name: 'CredentialsAdmin', query: { pageNum: newPage, sortBy: this.pagination.sortBy[0], sortOrder: this.pagination.sortDesc[0] ? 'desc' : 'asc' } })
    },

    sortTable() {
      this.$router.replace({ name: 'CredentialsAdmin', query: { pageNum: this.pagination.page, sortBy: this.pagination.sortBy[0], sortOrder: this.pagination.sortDesc[0] ? 'desc' : 'asc' } })
    },

    async searchCredentials() {
      this.dataTableLoading = true

      let realSortField = this.pagination.sortBy[0]
      if (realSortField === 'user') {
        realSortField = 'user.name'
      } else if (realSortField === 'email') {
        realSortField = 'user.email'
      }

      const pageRequest = {
        'page-num': this.pagination.page || 1,
        'page-size': this.pagination.itemsPerPage || 10,
        'sort-field': realSortField,
        'sort-order': this.pagination.sortDesc !== undefined ? (this.pagination.sortDesc[0] ? 'desc' : 'asc') : 'desc'
      }

      try {
        const page = this.isAdmin ? await credentials.listCredentials(null, this.search, pageRequest) : await credentials.listMyCredentials(null, this.search, pageRequest)
        this.credentialList = page.content
        this.totalItems = page.total
        this.pagination.page = page.pageNum

        // activationLoading
        this.credentialList.forEach(element => {
          this.$set(this.credentialTableActivationBtnLoading, element.id, false)
        })
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.dataTableLoading = false
      }
    },

    searchCredentialsWatcher: debounce(function() {
      this.searchCredentials()
    }, 500),

    showPasswordActions(item) {
      return (item.user.id === this.rsspUserId)
    },

    showAuthorizationModal(item, operation) {
      this.authorizationModal = true
      this.$nextTick(() => {
        this.$refs.authorizationModalForm.reset()
        this.selectedCredential = item
        this.selectedCredentialOperation = operation
        this.$nextTick(() => {
          if (operation === this.credentialOperation.ACTIVATE) {
            this.$refs.pwdField.focus()
          } else {
            this.$refs.oldPwdField.focus()
          }
        })
      })
    },

    async activateCredential(item) {
      if (item != null) {
        this.selectedCredential = item
      }

      this.credentialTableActivationBtnLoading[this.selectedCredential.id] = true

      const explicitCredential = this.isExplicitCredential(this.selectedCredential.authMode)
      const implicitCredential = this.isImplicitCredential(this.selectedCredential.authMode)
      this.authorizationModalBtnLoading = explicitCredential

      // this.implicitAuthorizationModalKey++
      this.ImplicitAuthorizationModalVisible = implicitCredential

      const generatedPuk = this.pinGenerator(12)
      // not using formatedd puk anymore because it caused bugs from the user when he thought tha "-" separator character is the part of the password
      // this.formattedPuk = this.getFormattedPuk(generatedPuk)
      this.formattedPuk = generatedPuk

      const req = {
        keyPuk: generatedPuk,
        keyPassword: this.expAuthPwd.pwd
      }

      try {
        await credentials.activateCredential(this.selectedCredential.id, implicitCredential ? {} : req, implicitCredential ? this.authorizationTimeoutMilis : null)
        this.searchCredentials()
        showToastSuccess(this.$t('toastMessage.succesfullyActivated'))

        // showGeneratedPukModal
        this.generatedPukModal = explicitCredential
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        if (errorMsg === 'Client side error. Error message: There are no enabled devices') {
          this.showNoDevicesModal = true
        } else {
          showToastError(errorMsg)
        }
      } finally {
        this.credentialTableActivationBtnLoading[this.selectedCredential.id] = false
        this.authorizationModalBtnLoading = false
        this.ImplicitAuthorizationModalVisible = false
        this.authorizationModal = false
      }
    },

    async changeCredentialPassword(item) {
      if (item != null) {
        this.selectedCredential = item
      }

      const implicitCredential = this.isImplicitCredential(this.selectedCredential.authMode)
      // this.implicitAuthorizationModalKey++
      this.ImplicitAuthorizationModalVisible = implicitCredential

      const req = {
        oldKeyPassword: this.expAuthPwd.oldPwd,
        newKeyPassword: this.expAuthPwd.pwd
      }

      try {
        await credentials.changePassword(this.selectedCredential.id, implicitCredential ? {} : req, implicitCredential ? this.authorizationTimeoutMilis : null)
        showToastSuccess(this.$t('toastMessage.succesfullyChanged'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.ImplicitAuthorizationModalVisible = false
        this.authorizationModal = false
      }
    },

    async unlockCredential(item) {
      if (item != null) {
        this.selectedCredential = item
      }

      const implicitCredential = this.isImplicitCredential(this.selectedCredential.authMode)
      // this.implicitAuthorizationModalKey++
      this.ImplicitAuthorizationModalVisible = implicitCredential

      const req = {
        keyPuk: this.expAuthPwd.oldPwd,
        newKeyPassword: this.expAuthPwd.pwd
      }

      try {
        await credentials.unlock(this.selectedCredential.id, implicitCredential ? {} : req, implicitCredential ? this.authorizationTimeoutMilis : null)
        showToastSuccess(this.$t('toastMessage.succesfullyChanged'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.ImplicitAuthorizationModalVisible = false
        this.authorizationModal = false
      }
    },

    async changeCredentialPuk(item) {
      if (item != null) {
        this.selectedCredential = item
      }

      const implicitCredential = this.isImplicitCredential(this.selectedCredential.authMode)
      // this.implicitAuthorizationModalKey++
      this.ImplicitAuthorizationModalVisible = implicitCredential

      const req = {
        oldKeyPuk: this.expAuthPwd.oldPwd,
        newKeyPuk: this.expAuthPwd.pwd,
        newKeyPassword: this.keyPwd.pwd
      }

      try {
        await credentials.changePuk(this.selectedCredential.id, implicitCredential ? {} : req, implicitCredential ? this.authorizationTimeoutMilis : null)
        showToastSuccess(this.$t('toastMessage.succesfullyChanged'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.ImplicitAuthorizationModalVisible = false
        this.authorizationModal = false
      }
    },

    async clearCredentialPreauthorization(id, e) {
      const conf = await showConfirmationMenu({ title: this.$t('dialog.confirmation.title'), posX: e.clientX, posY: e.clientY })
      if (!conf) {
        return
      }

      try {
        await credentials.clearCredentialPreauthorization(id)
        this.searchCredentials()
        showToastSuccess(this.$t('toastMessage.succesfullyClearedPreAuth'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    async deleteCredential(id, e) {
      const title = this.$t('dialog.confirmation.title')
      const conf = await showConfirmationMenu({ title: title, posX: e.clientX, posY: e.clientY })
      // const conf = await showConfirmationMenu({ title: this.$t('dialog.confirmation.title'), posX: e.clientX, posY: e.clientY })
      if (!conf) {
        return
      }

      try {
        await credentials.deleteCredential(id)
        this.searchCredentials()
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    pinGenerator(length) {
      var generatePassword = (
        length = 20,
        wishlist = '0123456789'
      ) =>
        Array.from(crypto.getRandomValues(new Uint32Array(length)))
          .map((x) => wishlist[x % wishlist.length])
          .join('')
      return generatePassword(12)
    },

    getFormattedPuk(puk) {
      const firstpart = puk.slice(0, 4)
      const secondPart = puk.slice(4, 8)
      const thirdPart = puk.slice(8, 12)
      return firstpart + '-' + secondPart + '-' + thirdPart
    },

    isCreatedCredential(status) {
      return status === CredentialStatus.CREATED
    },

    isActivatedCredential(status) {
      return status === CredentialStatus.ACTIVATED
    },

    isExplicitCredential(authMode) {
      return authMode === AuthMode.EXPLICIT
    },

    isImplicitCredential(authMode) {
      return authMode === AuthMode.IMPLICIT
    },

    isActiveCredentialOperation(operation) {
      return operation === this.credentialOperation.ACTIVATE
    },

    isChangePasswordCredentialOperation(operation) {
      return operation === this.credentialOperation.CHANGE_PASSWORD
    },

    isUnlockCredentialOperation(operation) {
      return operation === this.credentialOperation.UNLOCK
    },

    isChangePukCredentialOperation(operation) {
      return operation === this.credentialOperation.CHANGE_PUK
    },

    getCredentialOperationTitle(operation) {
      if (this.isActiveCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.activeTitle')
      } else
      if (this.isChangePasswordCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.changePasswordTitle')
      } else
      if (this.isUnlockCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.unlockPasswordTitle')
      } else
      if (this.isChangePukCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.changePUKTitle')
      }
    },

    getPasswordFieldLabel(operation) {
      if (this.isActiveCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.content.keyPassword') + '*'
      } else
      if (this.isChangePasswordCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.content.newKeyPassword') + '*'
      } else
      if (this.isUnlockCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.content.newKeyPassword') + '*'
      } else
      if (this.isChangePukCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.content.newKeyPuk') + '*'
      }
    },

    getPassworConfirmationFieldLabel(operation) {
      if (this.isActiveCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.content.keyPasswordConfirmation') + '*'
      } else
      if (this.isChangePasswordCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.content.newKeyPasswordConfirmation') + '*'
      } else
      if (this.isUnlockCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.content.newKeyPasswordConfirmation') + '*'
      } else
      if (this.isChangePukCredentialOperation(operation)) {
        return this.$t('credentials.modal.authorization.content.newKeyPukConfirmation') + '*'
      }
    },

    getChipStyle(item) {
      if (item.status === 'ACTIVATED') {
        return 'statusEnabled'
      } else {
        return 'statusInactive'
      }
    },

    getChipText(item) {
      return this.$t(`dataTable.content.credentials.statusEnum.${item.status.toLowerCase()}`)
    },

    getKeyPurposeText(item) {
      return this.$t(`credentials.content.keyPurposeLabels.${item.keyPurpose}`)
    },

    getAuthModeText(item) {
      if (item.authMode === 'EXPLICIT') {
        // todo localise
        return 'Explicit'
      } else
      if (item.authMode === 'IMPLICIT') {
        return 'Implicit'
      } else
      if (item.provider || item.isEvro || item.isAlfa) {
        return this.$t('credentials.content.importedAuthMode')
      } else {
        return ''
      }
    }

  }

}
</script>
